import type { AxiosResponse } from "axios";
import type { ApiResponse } from "../../models/ApiResponse";
import type { CreatePartnerRequest, CreatePartnerResponse, GetCountriesResponse, GetFeaturesResponse, GetMasterAccountDetailsResponse, GetPartnerSupportInformationResponse, GetRolesResponse, UpsertPartnerToMultiEnvironmentRequest, UpsertPartnerToMultiEnvironmentResponse, ValidateSubAccountRequest, ValidateSubAccountResponse } from "../../models/PartnerMaster";
import { isSuccessResponse } from "../../models/ApiResponse";
import type { IHttpWrapperService } from "../interface/IHttpWrapperService";
import type { IPartnerMasterService } from "../interface/IPartnerMasterService";
import { container, TYPES } from "../inversify.config";

export class PartnerMasterService implements IPartnerMasterService {
    private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

    async GetRoles(): Promise<GetRolesResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetRolesResponse>>  = await this.httpService.getRequest<GetRolesResponse>('partnermaster/getroles');
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch roles");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch roles: ${error.message}`);
            }
            throw new Error("Failed to fetch roles");
        }
    }

    async GetFeatures(): Promise<GetFeaturesResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetFeaturesResponse>> = await this.httpService.getRequest<GetFeaturesResponse>('partnermaster/getfeatures');
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch features");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch features: ${error.message}`);
            }
            throw new Error("Failed to fetch features");
        }
    }

    async CreatePartner(partnerData: CreatePartnerRequest): Promise<CreatePartnerResponse> {
        try {
            const response: AxiosResponse<ApiResponse<CreatePartnerResponse>> = await this.httpService.postRequest<CreatePartnerResponse, CreatePartnerRequest>('partnermaster/upsertpartnermasteraccount', partnerData);
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to create partner");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to create partner: ${error.message}`);
            }
            throw new Error("Failed to create partner");
        }
    }

    async ValidateSubAccount(request: ValidateSubAccountRequest): Promise<ValidateSubAccountResponse> {
        try {
            const response: AxiosResponse<ApiResponse<ValidateSubAccountResponse>> = await this.httpService.postRequest<ValidateSubAccountResponse, ValidateSubAccountRequest>('partnermaster/subaccount/validatesubaccountcode', request);
            
            // API returns plain boolean: false = valid, true = invalid
            // The boolean can be at response.data or response.data.data depending on the API wrapper
            let isValidBoolean: boolean;
            
            if (typeof response.data === 'boolean') {
                isValidBoolean = response.data;
            } else if (response.data.data !== undefined) {
                isValidBoolean = response.data.data as unknown as boolean;
            } else {
                // Fallback: treat as invalid
                isValidBoolean = true;
            }
            
            return {
                isValid: isValidBoolean,
                message: isValidBoolean ? 'Invalid account code' : 'Valid account code'
            };
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Network error during validation: ${error.message}`);
            }
            throw new Error("Network error during validation");
        }
    }

    async GetMasterAccountDetails(accountCode: string): Promise<GetMasterAccountDetailsResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetMasterAccountDetailsResponse>> = await this.httpService.getRequest<GetMasterAccountDetailsResponse>(`partnermaster/getMasterAccountDetails/14-1-${accountCode}`);
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch master account details");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch master account details: ${error.message}`);
            }
            throw new Error("Failed to fetch master account details");
        }
    }

    async UpsertPartnerToMultiEnvironment(request: UpsertPartnerToMultiEnvironmentRequest): Promise<UpsertPartnerToMultiEnvironmentResponse> {
        try {
            const response: AxiosResponse<ApiResponse<UpsertPartnerToMultiEnvironmentResponse>> = await this.httpService.postRequest<UpsertPartnerToMultiEnvironmentResponse, UpsertPartnerToMultiEnvironmentRequest>('partnermaster/upsertpartnertomultienvironment', request);
            if (isSuccessResponse(response.data)) {
                return {
                    isSuccess: true,
                    data: null,
                    message: response.data.message || "Partner upserted to target environments successfully."
                };
            } else {
                throw new Error(response.data.message || "Failed to upsert partner to environments");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to upsert partner to environments: ${error.message}`);
            }
            throw new Error("Failed to upsert partner to environments");
        }
    }

    async GetPartnerSupportInformation(partnerCode: string): Promise<GetPartnerSupportInformationResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetPartnerSupportInformationResponse>> = await this.httpService.postRequest<GetPartnerSupportInformationResponse, {}>(`partnermaster/getpartnersupportinformation/${partnerCode}`, {});
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch partner support information");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch partner support information: ${error.message}`);
            }
            throw new Error("Failed to fetch partner support information");
        }
    }

    async GetCountries(): Promise<GetCountriesResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetCountriesResponse>> = await this.httpService.getRequest<GetCountriesResponse>('partnermaster/country');
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch countries");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch countries: ${error.message}`);
            }
            throw new Error("Failed to fetch countries");
        }
    }
}